import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyseFiles, createFromAnalysis } from '../../service/SessionService';
import { AnalyseResponse, FileAnalysisResult, SessionCreationConfig } from '../../models/AnalyseResponse';

// ─── Style constants (matching other dashboard components) ───
const btn = {
  primary: 'gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition text-sm font-medium',
  secondary: 'border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition text-sm',
  danger: 'bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition text-sm font-medium',
};
const card = 'bg-white rounded-2xl shadow-sm p-6';

// Toggle component
const Toggle = ({ on, onToggle }: { on: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${on ? 'bg-blue-600' : 'bg-gray-300'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

// ─── Duplicate detection (filename only, no PDF read) ───
function extractDnNumber(name: string): string {
  const m = name.match(/[_\s](D\d+)[_\s]/i);
  return m ? m[1].toUpperCase() : name;
}

function extractFileTime(name: string): string {
  const stem = name.replace(/\.pdf$/i, '');
  const parts = stem.split(/[_\s]/);
  for (let i = parts.length - 1; i >= 0; i--) {
    if (/^\d{6}$/.test(parts[i])) return parts[i];
  }
  return '000000';
}

function deduplicateFiles(files: File[]): { kept: File[]; removed: File[] } {
  const map = new Map<string, { file: File; time: string }>();
  const removed: File[] = [];
  for (const file of files) {
    const key = extractDnNumber(file.name);
    const time = extractFileTime(file.name);
    if (map.has(key)) {
      const existing = map.get(key)!;
      if (time > existing.time) {
        removed.push(existing.file);
        map.set(key, { file, time });
      } else {
        removed.push(file);
      }
    } else {
      map.set(key, { file, time });
    }
  }
  return { kept: Array.from(map.values()).map(v => v.file), removed };
}

// ─── Yesterday in DD.MM.YYYY ───
function getYesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function isoToDisplay(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

// ─── Status badge ───
const StatusBadge = ({ result, filterOt }: { result: FileAnalysisResult; filterOt: boolean }) => {
  if (result.status === 'DUPLICATE') return <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">Duplicate</span>;
  if (result.status === 'DATE_SKIPPED') return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">Date skipped</span>;
  if (result.status === 'CONVERSION_FAILED') return <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">Failed</span>;
  if (!filterOt) return <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">Session</span>;
  if (result.sessionGroup === 'STOCK') return <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Stock</span>;
  if (result.sessionGroup === 'EME_VOR') return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">EME / VOR</span>;
  return <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">Unknown</span>;
};

// ─── Main component ───
const CreateSessionWizard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1 state
  const [allFiles, setAllFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [filterDate, setFilterDate] = useState(true);
  const [dateMode, setDateMode] = useState<'yesterday' | 'custom'>('yesterday');
  const [customDate, setCustomDate] = useState(''); // YYYY-MM-DD
  const [filterDup, setFilterDup] = useState(true);
  const [filterOt, setFilterOt] = useState(true);

  // Step 2 state
  const [analysing, setAnalysing] = useState(false);
  const [analyseResult, setAnalyseResult] = useState<AnalyseResponse | null>(null);
  const [analyseError, setAnalyseError] = useState('');

  // Step 3 state — session titles editable
  const [stockTitle, setStockTitle] = useState('STOCK');
  const [emevTitle, setEmevTitle] = useState('EME/VOR');
  const [mixedTitle, setMixedTitle] = useState('Stock/EV');

  // Step 4 state
  const [createdSessions, setCreatedSessions] = useState<any[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // ─── File handling ───
  const addFiles = (incoming: File[]) => {
    const pdfs = incoming.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    const existing = new Set(allFiles.map(f => f.name));
    const toAdd = pdfs.filter(f => !existing.has(f.name));
    if (toAdd.length) setAllFiles(prev => [...prev, ...toAdd]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  // ─── Analyse ───
  const resolvedTargetDate = (): string | null => {
    if (!filterDate) return null;
    if (dateMode === 'custom' && customDate) return isoToDisplay(customDate);
    return getYesterday();
  };

  const handleAnalyse = async () => {
    setAnalyseError('');
    setAnalysing(true);
    setStep(2);

    // Client-side duplicate detection first (fast, no upload)
    let filesToSend = allFiles;
    if (filterDup) {
      const { kept } = deduplicateFiles(allFiles);
      filesToSend = kept;
    }

    const formData = new FormData();
    filesToSend.forEach(f => formData.append('files', f));
    formData.append('filterDate', String(filterDate));
    formData.append('filterOrderType', String(filterOt));
    const target = resolvedTargetDate();
    if (target) formData.append('targetDate', target);

    try {
      const result = await analyseFiles(formData);
      // Inject client-side duplicates into the result files list
      if (filterDup) {
        const { removed } = deduplicateFiles(allFiles);
        const dupResults = removed.map(f => ({
          fileName: f.name,
          processingDate: null,
          orderTypeCodes: null,
          sessionGroup: null,
          status: 'DUPLICATE' as const,
          deliveryNote: null,
        }));
        result.files = [...result.files, ...dupResults];
        result.duplicatesRemoved = removed.length;
      }
      setAnalyseResult(result);
    } catch (err: any) {
      setAnalyseError(err?.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setAnalysing(false);
    }
  };

  // ─── Create sessions ───
  const handleCreate = async () => {
    if (!analyseResult) return;
    setCreating(true);
    setCreateError('');

    const includedFiles = analyseResult.files.filter(f => f.status === 'INCLUDED');
    const sessions: SessionCreationConfig[] = [];

    if (filterOt) {
      const stockFileNames = includedFiles.filter(f => f.sessionGroup === 'STOCK').map(f => f.fileName);
      const emevFileNames = includedFiles.filter(f => f.sessionGroup === 'EME_VOR').map(f => f.fileName);
      const unknownFileNames = includedFiles.filter(f => f.sessionGroup === 'UNKNOWN').map(f => f.fileName);
      if (stockFileNames.length) sessions.push({ title: stockTitle, group: 'STOCK', fileNames: stockFileNames });
      if (emevFileNames.length) sessions.push({ title: emevTitle, group: 'EME_VOR', fileNames: emevFileNames });
      if (unknownFileNames.length) sessions.push({ title: 'Unknown', group: 'UNKNOWN', fileNames: unknownFileNames });
    } else {
      const allIncluded = includedFiles.map(f => f.fileName);
      if (allIncluded.length) sessions.push({ title: mixedTitle, group: 'ALL', fileNames: allIncluded });
    }

    if (!sessions.length) {
      setCreateError('No files available to create sessions.');
      setCreating(false);
      return;
    }

    try {
      const created = await createFromAnalysis({ analysisId: analyseResult.analysisId, sessions });
      setCreatedSessions(created);
      setStep(4);
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || 'Failed to create sessions. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const includedFiles = analyseResult?.files.filter(f => f.status === 'INCLUDED') ?? [];
  const stockFiles = includedFiles.filter(f => f.sessionGroup === 'STOCK');
  const emevFiles = includedFiles.filter(f => f.sessionGroup === 'EME_VOR');

  // ─── Step 1: Upload + Filters ───
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className={card}>
        <div className="flex items-center mb-4">
          <button onClick={() => navigate('/dashboard/scanning-session')} className="mr-4 text-gray-600 hover:text-gray-900">
            <i className="fas fa-arrow-left" />
          </button>
          <h2 className="text-xl font-semibold text-gray-800">Create Session</h2>
        </div>

        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
          onDragOver={e => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
        >
          <input ref={fileInputRef} type="file" accept=".pdf" multiple className="hidden" onChange={handleFileInput} />
          <i className="fas fa-cloud-upload-alt text-4xl text-gray-400 mb-3" />
          <p className="text-gray-500 mb-3 text-sm">Drag & drop PDF files here or click to browse</p>
          <button type="button" className={btn.primary} onClick={() => fileInputRef.current?.click()}>
            Select Files
          </button>
          {allFiles.length > 0 && (
            <p className="mt-3 text-sm font-medium text-blue-600">{allFiles.length} file(s) selected</p>
          )}
        </div>

        {/* File list */}
        {allFiles.length > 0 && (
          <div className="mt-4 border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-48 overflow-y-auto">
            {allFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2">
                <i className="fas fa-file-pdf text-red-400 text-sm" />
                <span className="flex-1 text-sm text-gray-700 truncate">{f.name}</span>
                <span className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</span>
                <button
                  onClick={() => setAllFiles(prev => prev.filter((_, idx) => idx !== i))}
                  className="text-red-500 hover:text-red-700 text-xs bg-red-50 px-2 py-1 rounded"
                >Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      {allFiles.length > 0 && (
        <div className={card}>
          <h3 className="text-base font-semibold text-gray-800 mb-4">Smart Filters</h3>
          <div className="space-y-4">

            {/* Date filter */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Date Filter</p>
                <p className="text-xs text-gray-500 mt-0.5">Keep only files matching the processing date</p>
                {filterDate && (
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="radio" name="dateMode" checked={dateMode === 'yesterday'}
                        onChange={() => setDateMode('yesterday')} />
                      Yesterday ({getYesterday()})
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="radio" name="dateMode" checked={dateMode === 'custom'}
                        onChange={() => setDateMode('custom')} />
                      Custom date
                    </label>
                    {dateMode === 'custom' && (
                      <input type="date" value={customDate}
                        onChange={e => setCustomDate(e.target.value)}
                        className="text-xs border border-gray-300 rounded px-2 py-1" />
                    )}
                  </div>
                )}
              </div>
              <Toggle on={filterDate} onToggle={() => setFilterDate(v => !v)} />
            </div>

            <hr className="border-gray-100" />

            {/* Duplicate filter */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Duplicate Filter</p>
                <p className="text-xs text-gray-500 mt-0.5">Remove duplicates by DN number, keep the most recent</p>
              </div>
              <Toggle on={filterDup} onToggle={() => setFilterDup(v => !v)} />
            </div>

            <hr className="border-gray-100" />

            {/* Order Type filter */}
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700">Order Type Filter</p>
                <p className="text-xs text-gray-500 mt-0.5">Split into Stock and EME/VOR sessions by order type</p>
              </div>
              <Toggle on={filterOt} onToggle={() => setFilterOt(v => !v)} />
            </div>
          </div>
        </div>
      )}

      {allFiles.length > 0 && (
        <div className="flex justify-end">
          <button className={`${btn.primary} px-6`} onClick={handleAnalyse}>
            <i className="fas fa-filter mr-2" />Analyse Files
          </button>
        </div>
      )}
    </div>
  );

  // ─── Step 2: Analysing / Results ───
  const renderStep2 = () => {
    if (analysing) return (
      <div className={`${card} text-center py-16`}>
        <div className="loader border-t-4 border-blue-500 rounded-full w-12 h-12 animate-spin mx-auto mb-4" />
        <p className="text-gray-600 font-medium">Analysing files...</p>
        <p className="text-gray-400 text-sm mt-1">Converting PDFs and extracting metadata</p>
      </div>
    );

    if (analyseError) return (
      <div className={card}>
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation text-red-500" />
          </div>
          <h3 className="font-semibold text-gray-800 mb-2">Analysis Failed</h3>
          <p className="text-gray-500 text-sm mb-4">{analyseError}</p>
          <button className={btn.secondary} onClick={() => { setStep(1); setAnalyseError(''); }}>Go Back</button>
        </div>
      </div>
    );

    if (!analyseResult) return null;

    return (
      <div className="space-y-6">
        <div className={card}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Analysis Results</h2>
            <button className={btn.secondary} onClick={() => setStep(1)}>
              <i className="fas fa-arrow-left mr-2" />Back
            </button>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total uploaded', value: analyseResult.totalUploaded, color: 'text-gray-800' },
              { label: 'Duplicates removed', value: analyseResult.duplicatesRemoved, color: 'text-amber-600' },
              { label: 'Date skipped', value: analyseResult.dateSkipped, color: 'text-amber-600' },
              { label: 'Ready', value: includedFiles.length, color: 'text-green-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-gray-50 rounded-xl p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          {/* Warnings */}
          <div className="space-y-2 mb-6">
            {analyseResult.dateSkipped > 0 && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-xl text-sm text-amber-800">
                <i className="fas fa-exclamation-triangle mr-2" />
                {analyseResult.dateSkipped} file(s) skipped — processing date does not match the filter
              </div>
            )}
            {analyseResult.duplicatesRemoved > 0 && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-xl text-sm text-amber-800">
                <i className="fas fa-copy mr-2" />
                {analyseResult.duplicatesRemoved} duplicate(s) removed — most recent kept
              </div>
            )}
            {analyseResult.conversionFailed > 0 && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-r-xl text-sm text-red-800">
                <i className="fas fa-times-circle mr-2" />
                {analyseResult.conversionFailed} file(s) could not be converted
              </div>
            )}
            {includedFiles.length > 0 && (
              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-r-xl text-sm text-green-800">
                <i className="fas fa-check-circle mr-2" />
                {includedFiles.length} file(s) ready
                {filterOt && ` — ${analyseResult.stockCount} Stock, ${analyseResult.emevCount} EME/VOR`}
              </div>
            )}
          </div>

          {/* File list */}
          <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-72 overflow-y-auto">
            {analyseResult.files.map((f, i) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 ${f.status !== 'INCLUDED' ? 'opacity-40' : ''}`}>
                <i className={`fas fa-file-pdf text-sm ${f.status === 'INCLUDED' ? 'text-red-400' : 'text-gray-400'}`} />
                <span className="flex-1 text-xs text-gray-700 font-mono truncate">{f.fileName}</span>
                {f.processingDate && <span className="text-xs text-gray-400">{f.processingDate}</span>}
                <StatusBadge result={f} filterOt={filterOt} />
              </div>
            ))}
          </div>
        </div>

        {includedFiles.length > 0 && (
          <div className="flex justify-end">
            <button className={`${btn.primary} px-6`} onClick={() => setStep(3)}>
              Preview Sessions <i className="fas fa-arrow-right ml-2" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // ─── Step 3: Session Preview ───
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className={card}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Session Preview</h2>
          <button className={btn.secondary} onClick={() => setStep(2)}>
            <i className="fas fa-arrow-left mr-2" />Back
          </button>
        </div>

        {filterOt ? (
          <div className="space-y-4">
            {stockFiles.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-green-800 text-sm">
                    <i className="fas fa-boxes mr-2" />Stock — {stockFiles.length} file(s)
                  </p>
                  <input
                    type="text"
                    value={stockTitle}
                    onChange={e => setStockTitle(e.target.value)}
                    className="text-sm border border-green-300 rounded px-2 py-1 bg-white text-gray-700 w-32 text-right"
                    placeholder="Session title"
                  />
                </div>
                <div className="text-xs text-green-700 font-mono space-y-0.5">
                  {stockFiles.map(f => <div key={f.fileName}>{f.fileName}</div>)}
                </div>
              </div>
            )}
            {emevFiles.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-blue-800 text-sm">
                    <i className="fas fa-truck mr-2" />EME/VOR — {emevFiles.length} file(s)
                  </p>
                  <input
                    type="text"
                    value={emevTitle}
                    onChange={e => setEmevTitle(e.target.value)}
                    className="text-sm border border-blue-300 rounded px-2 py-1 bg-white text-gray-700 w-32 text-right"
                    placeholder="Session title"
                  />
                </div>
                <div className="text-xs text-blue-700 font-mono space-y-0.5">
                  {emevFiles.map(f => <div key={f.fileName}>{f.fileName}</div>)}
                </div>
              </div>
            )}
            {analyseResult && analyseResult.unknownCount > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <p className="text-sm text-gray-600">
                  <i className="fas fa-question-circle mr-2" />
                  {analyseResult.unknownCount} file(s) with unknown order type will be created as a separate session
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-amber-800 text-sm">
                <i className="fas fa-layer-group mr-2" />{includedFiles.length} file(s)
              </p>
              <input
                type="text"
                value={mixedTitle}
                onChange={e => setMixedTitle(e.target.value)}
                className="text-sm border border-amber-300 rounded px-2 py-1 bg-white text-gray-700 w-32 text-right"
                placeholder="Session title"
              />
            </div>
            <div className="text-xs text-amber-700 font-mono space-y-0.5">
              {includedFiles.map(f => <div key={f.fileName}>{f.fileName}</div>)}
            </div>
          </div>
        )}

        {createError && (
          <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-3 rounded-r-xl text-sm text-red-800">
            <i className="fas fa-exclamation-circle mr-2" />{createError}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button className={`${btn.primary} px-6`} onClick={handleCreate} disabled={creating}>
          {creating ? (
            <><i className="fas fa-spinner fa-spin mr-2" />Creating...</>
          ) : (
            <><i className="fas fa-check mr-2" />Create Sessions</>
          )}
        </button>
      </div>
    </div>
  );

  // ─── Step 4: Success ───
  const renderStep4 = () => (
    <div className={`${card} text-center`}>
      <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="fas fa-check text-green-600 text-xl" />
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        {createdSessions.length} Session{createdSessions.length !== 1 ? 's' : ''} Created
      </h2>
      <div className="mt-4 space-y-3 text-left max-w-sm mx-auto">
        {createdSessions.map((s, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-4">
            <p className="font-medium text-gray-800 text-sm">{s.title}</p>
            <p className="text-xs text-gray-500 mt-1">
              {s.deliveryNotes?.length ?? 0} delivery note(s) · {new Date(s.createdDate).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-3 mt-6">
        <button className={btn.primary} onClick={() => navigate('/dashboard/scanning-session')}>
          Go to Sessions
        </button>
        <button className={btn.secondary} onClick={() => {
          setStep(1); setAllFiles([]); setAnalyseResult(null);
          setCreatedSessions([]); setCreateError('');
        }}>
          Create More
        </button>
      </div>
    </div>
  );

  return (
    <div className="dashboard-section max-w-3xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6 text-xs text-gray-500">
        {(['Upload', 'Results', 'Preview', 'Done'] as const).map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium
              ${step === i + 1 ? 'gradient-bg text-white' : step > i + 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
              {step > i + 1 ? <i className="fas fa-check text-xs" /> : i + 1}
            </div>
            <span className={step === i + 1 ? 'text-gray-800 font-medium' : ''}>{label}</span>
            {i < 3 && <i className="fas fa-chevron-right text-gray-300" />}
          </div>
        ))}
      </div>

      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
};

export default CreateSessionWizard;
